<script setup>
import { h, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import BranchSwitcherCard from 'dashboard/components-next/branch-switcher/BranchSwitcherCard.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import ChatsMultiChannelIcon from './ChatsMultiChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import TasksBell from './TasksBell.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

// Permisos granulares Fase 3 · si role=admin o legacy mode → can() devuelve true (no afecta)
const { can, isSuperAdmin, role } = usePermissions();

// Rol Fotógrafo (retoque_scope · Victor 2026-06-07): NO ve Chats ni Planes en el
// menú (solo Leads). Se lee el flag directo de feature_permissions (no por can(),
// que daría true a super_admin). Los items con `hideForRetoque` se ocultan.
const currentAccount = useMapGetter('getCurrentAccount');
const isRetoqueScoped = computed(
  () => !isSuperAdmin.value && currentAccount.value?.feature_permissions?.retoque_scope === true
);
// Roles Fotógrafo (retoque_scope) y Video (fotografo_scope · Victor 2026-06-08):
// el menú muestra ÚNICAMENTE Leads. Abren el chat del cliente desde la tarjeta
// del Lead. Se ocultan Inicio, Informes, Planes, Chats y Ajustes.
const isFotoOrVideoScoped = computed(() => {
  if (isSuperAdmin.value) return false;
  const fp = currentAccount.value?.feature_permissions || {};
  return fp.retoque_scope === true || fp.fotografo_scope === true;
});
// Rol Cuentas por Cobrar (Victor 2026-06-10): menú SOLO Chat + Leads.
const isCuentasCobrar = computed(() => {
  if (isSuperAdmin.value) return false;
  const fp = currentAccount.value?.feature_permissions || {};
  return fp.cuentas_cobrar_scope === true;
});
// Roles que NO ven Planes en el menú: Fotógrafo (retoque) y Vendedor (hide_planes).
// Planes usa el mismo permiso que Leads, por eso se controla con un flag aparte.
const hidePlanesRole = computed(() => {
  if (isSuperAdmin.value) return false;
  const fp = currentAccount.value?.feature_permissions || {};
  return fp.retoque_scope === true || fp.hide_planes === true;
});

// Filtra recursivamente items de menú según permisos.
// Items SIN propiedad `permission` siempre visibles. Items con `permission` solo si can().
// `superAdminOnly: true` → solo visible para super_admin (regla 2026-05-13 Victor).
// Si un item con children termina con 0 children visibles → también se oculta.
function filterByPermissions(items) {
  return items
    .map(item => {
      if (item.superAdminOnly && !isSuperAdmin.value) return null;
      // Fotógrafo y Video: el menú es SOLO Leads (item con keepForFotoVideo).
      if (isFotoOrVideoScoped.value && !item.keepForFotoVideo) return null;
      // Rol Cuentas por Cobrar: menú SOLO Chat + Leads (Victor 2026-06-10).
      if (isCuentasCobrar.value && !item.keepForCuentasCobrar) return null;
      // Rol Fotógrafo (retoque): oculta Chats (solo ve Leads).
      if (item.hideForRetoque && isRetoqueScoped.value) return null;
      // Vendedor y Fotógrafo: ocultan Planes (Leads usa el mismo permiso).
      if (item.hidePlanes && hidePlanesRole.value) return null;
      // adminOnly → solo administrator o super_admin (oculta el icono al resto)
      if (item.adminOnly && !(role.value === 'administrator' || isSuperAdmin.value)) return null;
      if (item.children?.length) {
        const filteredChildren = filterByPermissions(item.children);
        if (filteredChildren.length === 0 && item.permission && !can(item.permission)) return null;
        if (filteredChildren.length === 0 && !item.to) return null; // sin children visibles y sin ruta propia
        return { ...item, children: filteredChildren };
      }
      if (item.permission && !can(item.permission)) return null;
      return item;
    })
    .filter(Boolean);
}

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

// Helpers para el sidebar Kommo-style (icon arriba + label debajo).
const route = useRoute();
const router = useRouter();

function resolveItemTo(item) {
  if (item.to) return item.to;
  if (item.children?.length) {
    const firstChildWithTo = item.children.find(c => c.to);
    return firstChildWithTo?.to || null;
  }
  return null;
}

function isItemActive(item) {
  const currentName = route.name;
  const matchesEntry = entry => {
    if (entry.activeOn?.includes(currentName)) return true;
    if (entry.to?.name === currentName) return true;
    return false;
  };
  if (matchesEntry(item)) return true;
  if (item.children) {
    for (const child of item.children) {
      if (matchesEntry(child)) return true;
    }
  }
  return false;
}

// El contador del ícono Chats lo maneja ChatsMultiChannelIcon (estilo M1.B P2).

// Items que abren popover al click en lugar de navegar directo.
function isPopoverItem(item) {
  return item.name === 'Settings';
}

const settingsPopoverOpen = ref(false);
function toggleSettingsPopover() {
  settingsPopoverOpen.value = !settingsPopoverOpen.value;
}
function closeSettingsPopover() {
  settingsPopoverOpen.value = false;
}
const settingsItem = computed(() =>
  menuItems.value.find(i => i.name === 'Settings')
);

// Drawer de Ajustes (Propuesta 1 · Victor 2026-05-15) · agrupa los ítems
// visibles en 2 secciones. Cualquier ítem no listado cae en "Otros".
const SETTINGS_GROUPS = [
  { label: 'Equipo', names: ['Settings Agents', 'Settings Teams', 'Settings Branches'] },
  {
    label: 'Canales & contenido',
    names: [
      'Settings Inboxes',
      'Settings Labels',
      'Settings Salesbots',
      'Settings Reservation Plans',
      'Settings Message Templates',
      'Settings Canned Responses',
      'Settings Integrations',
    ],
  },
];
const settingsGroups = computed(() => {
  const children = settingsItem.value?.children || [];
  const groups = SETTINGS_GROUPS.map(g => ({
    label: g.label,
    items: g.names.map(n => children.find(c => c.name === n)).filter(Boolean),
  })).filter(g => g.items.length);
  const known = new Set(SETTINGS_GROUPS.flatMap(g => g.names));
  const rest = children.filter(c => !known.has(c.name));
  if (rest.length) groups.push({ label: 'Otros', items: rest });

  // Forzar "Plantillas de Mensajes" (Victor 2026-05-23): se inserta directo en
  // "Canales & contenido" justo después de Salesbots, sin depender del armado
  // por children (que lo estaba descartando). A prueba de balas.
  const canales = groups.find(g => g.label === 'Canales & contenido');
  if (canales && !canales.items.some(i => i?.name === 'Settings Message Templates')) {
    const idx = canales.items.findIndex(i => i?.name === 'Settings Salesbots');
    canales.items.splice(idx >= 0 ? idx + 1 : canales.items.length, 0, {
      name: 'Settings Message Templates',
      label: 'Plantillas de Mensajes',
      icon: 'i-lucide-sparkles',
      to: accountScopedRoute('message_templates_list'),
    });
  }
  return groups;
});

function navigateToChild(child) {
  if (child.to) {
    router.push(child.to);
    closeSettingsPopover();
  }
}

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode).
// On desktop, forzamos colapsado siempre (sidebar Kommo-style 80px fijo).
const isEffectivelyCollapsed = computed(() => !isMobile.value);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const activeBranchId = useMapGetter('branches/getActiveBranchId');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

// Aislamiento por sucursal · 2026-05-20 (Victor): el sidebar solo muestra
// inboxes de la sucursal activa del switcher. Sin sucursal activa muestra todos
// (fallback inicial).
const sortedInboxes = computed(() => {
  const all = inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name));
  const branchId = Number(activeBranchId.value);
  if (!branchId) return all;
  return all.filter(i => Number(i.branch_id) === branchId);
});

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  const rawItems = [
    {
      name: 'Inicio',
      label: 'Inicio',
      icon: 'i-lucide-house',
      to: accountScopedRoute('home_blank'),
      activeOn: ['home_blank'],
      permission: 'home.view',
    },
    {
      name: 'Reports',
      label: 'Informes',
      icon: 'i-lucide-chart-spline',
      permission: 'reports.view',
      to: accountScopedRoute('advanced_metrics_reports'),
      activeOn: [
        'account_overview_reports',
        'advanced_metrics_reports',
        'conversation_reports',
        'csat_reports',
        'sla_reports',
        'bot_reports',
        'agent_reports',
        'label_reports',
        'inbox_reports',
        'team_reports',
      ],
    },
    {
      name: 'Planes',
      label: 'Planes',
      icon: 'i-lucide-camera',
      to: accountScopedRoute('planes_home'),
      activeOn: ['planes_home'],
      permission: 'leads_deals.view',
      hidePlanes: true,
    },
    {
      name: 'Pipelines',
      label: 'Leads',
      icon: 'i-lucide-target',
      to: accountScopedRoute('pipelines_home'),
      activeOn: ['pipelines_home'],
      permission: 'leads_deals.view',
      keepForFotoVideo: true, // único item del menú para Fotógrafo/Video
      keepForCuentasCobrar: true,
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CHATS'),
      icon: h(ChatsMultiChannelIcon, { size: 16 }),
      permission: 'chat.view',
      hideForRetoque: true,
      keepForCuentasCobrar: true,
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      // Solo admin / super_admin ven el icono de Ajustes (Victor 2026-05-15).
      adminOnly: true,
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
          permission: 'account_settings.view',
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_kanban'),
          permission: 'agents.view',
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
          permission: 'teams.view',
        },
        {
          name: 'Settings Branches',
          label: 'Sucursales',
          icon: 'i-lucide-map-pin',
          activeOn: ['settings_branches_list'],
          to: accountScopedRoute('settings_branches_list'),
          permission: 'branches.view',
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          activeOn: [
            'settings_inbox_list',
            'settings_inbox_show',
            'settings_inbox_new',
            'settings_inbox_finish',
            'settings_inboxes_page_channel',
            'settings_inboxes_add_agents',
          ],
          to: accountScopedRoute('settings_inbox_list'),
          permission: 'inboxes.view',
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
          permission: 'labels.view',
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
          permission: 'custom_attributes.view',
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
          permission: 'automation.view',
        },
        {
          name: 'Settings Salesbots',
          label: 'Salesbot por etapa',
          icon: 'i-lucide-zap',
          to: accountScopedRoute('salesbots_index'),
          permission: 'salesbot.view',
        },
        {
          name: 'Settings Reservation Plans',
          label: 'Catálogo de Planes',
          icon: 'i-lucide-package',
          to: accountScopedRoute('reservation_plans_index'),
          permission: 'salesbot.view',
        },
        {
          name: 'Settings Round Robin Universal',
          label: 'Round Robin Universal',
          icon: 'i-lucide-shuffle',
          to: accountScopedRoute('settings_round_robin_universal'),
          superAdminOnly: true,
        },
        // V2 · 2026-05-14 (Victor) · entry oculto · ahora se gestiona via
        // AgentPermissionsModal (Ajustes → Agentes → editar). Ruta sigue
        // accesible directamente por URL si se necesita.
        // {
        //   name: 'Settings Agent Branch Access',
        //   label: 'Permisos por Agente',
        //   icon: 'i-lucide-user-cog',
        //   to: accountScopedRoute('settings_agent_branch_access'),
        //   superAdminOnly: true,
        // },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
          permission: 'bots.view',
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
          permission: 'macros.view',
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
          permission: 'canned_responses.view',
        },
        {
          name: 'Settings Message Templates',
          label: 'Plantillas de Mensajes',
          icon: 'i-lucide-sparkles',
          to: accountScopedRoute('message_templates_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
          permission: 'integrations.view',
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
          permission: 'audit_logs.view',
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
          permission: 'custom_roles.view',
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
          permission: 'sla.view',
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
          permission: 'workflow.view',
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ].filter(item =>
        // Victor 2026-05-15 · menú Ajustes reducido para evitar ruido visual.
        // Solo estos 8 ítems. El resto sigue accesible por URL directa.
        [
          'Settings Agents',
          'Settings Teams',
          'Settings Branches',
          'Settings Inboxes',
          'Settings Labels',
          'Settings Salesbots',
          'Settings Canned Responses',
          'Settings Integrations',
        ].includes(item.name)
      ),
    },
  ];

  // Aplica filtro de permisos · si algún item no se permite, lo oculta del menú.
  // Admins/legacy mode pasan transparentes (can() devuelve true).
  return filterByPermissions(rawItems);
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-black flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-zinc-900"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: '80px' }"
  >
    <!-- BranchSwitcherCard arriba (avatar custom de la sucursal) -->
    <div class="px-2 pt-2 pb-2 border-b border-zinc-900 flex justify-center">
      <BranchSwitcherCard :collapsed="isEffectivelyCollapsed" />
    </div>


    <!-- Account switcher + Search + Compose · oculto para ganar espacio vertical -->
    <section
      v-if="false"
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <Logo class="size-4" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align-position="right" @close="onComposeClose">
          <template #trigger="{ toggle, isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
              @click="onComposeOpen(toggle)"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <!-- Sidebar Kommo-style: icon arriba + label debajo · activo bg blanco -->
    <nav class="overflow-y-auto flex-grow no-scrollbar pb-2 relative">
      <ul class="flex flex-col m-0 list-none w-full">
        <li class="w-full border-b border-zinc-900/60">
          <TasksBell />
        </li>
        <li v-for="item in menuItems" :key="item.name" class="w-full">
          <!-- Items con popover (Ajustes) -->
          <button
            v-if="isPopoverItem(item)"
            id="settings-popover-trigger"
            type="button"
            class="w-full flex flex-col items-center gap-1 py-2.5 px-1 transition-colors"
            :class="
              isItemActive(item) || settingsPopoverOpen
                ? 'bg-amber-400/10 text-amber-400'
                : 'text-zinc-500 hover:bg-white/5'
            "
            @click="toggleSettingsPopover"
          >
            <div class="relative">
              <span
                v-if="typeof item.icon === 'string'"
                class="size-6 block"
                :class="item.icon"
              />
              <component :is="item.icon" v-else :size="22" />
            </div>
            <span
              class="text-[10px] leading-tight"
              :class="(isItemActive(item) || settingsPopoverOpen) ? 'font-semibold' : ''"
            >{{ item.label }}</span>
          </button>
          <!-- Items con navegación directa -->
          <RouterLink
            v-else-if="resolveItemTo(item)"
            :to="resolveItemTo(item)"
            custom
            v-slot="{ navigate }"
          >
            <button
              type="button"
              class="w-full flex flex-col items-center gap-1 py-2.5 px-1 transition-colors"
              :class="
                isItemActive(item)
                  ? 'bg-amber-400/10 text-amber-400'
                  : 'text-zinc-500 hover:bg-white/5'
              "
              @click="navigate"
            >
              <div class="relative">
                <span
                  v-if="typeof item.icon === 'string'"
                  class="size-6 block"
                  :class="item.icon"
                />
                <component :is="item.icon" v-else :size="22" />
                <!-- El badge del contador vive dentro del propio ícono
                     (ChatsMultiChannelIcon · estilo M1.B P2). Aquí no se
                     duplica para evitar dos badges. -->
              </div>
              <span
                class="text-[10px] leading-tight"
                :class="isItemActive(item) ? 'font-semibold' : ''"
              >{{ item.label }}</span>
            </button>
          </RouterLink>
        </li>
      </ul>
    </nav>

    <!-- Drawer de Ajustes (Propuesta 1 · Victor 2026-05-15) -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-200"
        leave-active-class="transition-opacity duration-150"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          v-if="settingsPopoverOpen && settingsItem"
          class="fixed inset-0 z-[60] bg-black/55"
          @click="closeSettingsPopover"
        />
      </Transition>
      <Transition
        enter-active-class="transition-transform duration-200 ease-out"
        leave-active-class="transition-transform duration-150 ease-in"
        enter-from-class="-translate-x-full"
        leave-to-class="-translate-x-full"
      >
        <aside
          v-if="settingsPopoverOpen && settingsItem"
          class="fixed left-0 top-0 bottom-0 z-[61] w-[300px] bg-n-background border-r border-n-weak shadow-2xl flex flex-col"
        >
          <div class="px-5 py-4 border-b border-n-weak flex items-center gap-2.5 shrink-0">
            <span class="i-lucide-bolt size-[18px] text-n-slate-11" />
            <span class="text-[15px] font-bold text-n-slate-12">{{ settingsItem.label }}</span>
            <button
              class="ml-auto size-7 rounded-full bg-n-alpha-1 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 flex items-center justify-center text-base leading-none"
              @click="closeSettingsPopover"
            >×</button>
          </div>
          <nav class="flex-1 overflow-y-auto p-2">
            <template v-for="group in settingsGroups" :key="group.label">
              <div class="px-3 pt-4 pb-1.5 text-[9.5px] font-bold uppercase tracking-wider text-n-slate-10">
                {{ group.label }}
              </div>
              <button
                v-for="child in group.items"
                :key="child.name"
                type="button"
                class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] text-left transition-colors"
                :class="
                  (child.activeOn?.includes(route.name) || child.to?.name === route.name)
                    ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                    : 'text-n-slate-11 hover:bg-n-alpha-1 dark:hover:bg-n-slate-9/30'
                "
                @click="navigateToChild(child)"
              >
                <span
                  v-if="typeof child.icon === 'string'"
                  class="size-4 shrink-0 opacity-75"
                  :class="child.icon"
                />
                <span class="truncate">{{ child.label }}</span>
              </button>
            </template>
          </nav>
        </aside>
      </Transition>
    </Teleport>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-black to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
